import { type ErrorInfo, StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";

import { setupAxiosInterceptors } from "@/utils/axiosInterceptor.ts";
setupAxiosInterceptors();

import { ErrorBoundary } from "react-error-boundary";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import ErrorBoundaryFallback from "./components/error-boundary.tsx";

import "@radix-ui/themes/styles.css";
import { Theme } from "@radix-ui/themes";
import { ToastProvider, ToastViewport } from "./components/ui/toast.tsx";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext.tsx";

const queryClient = new QueryClient();

const logError = (error: Error, info: ErrorInfo) => {
  console.error(error, info);
};

function RadixThemeWrapper({ children }: { children: ReactNode }) {
  const { theme, forcedLight } = useTheme();
  return <Theme appearance={forcedLight ? "light" : theme}>{children}</Theme>;
}

// biome-ignore lint/style/noNonNullAssertion: Default root element is always present
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary
          FallbackComponent={ErrorBoundaryFallback}
          onError={logError}
        >
          <ToastProvider>
            <RadixThemeWrapper>
              <App />
            </RadixThemeWrapper>
            <ToastViewport />
          </ToastProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>
);
